import { useRef } from 'react';
import { StyleSheet } from './platform';
import { useTheme } from './useTheme';
import type { Theme } from './theme';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single style value — either a plain primitive or a `$ns.path` token. */
export type JsonStyleValue = string | number | boolean | null | undefined;

/** A flat style object whose values may contain `$theme.*` / `$StyleSheet.*` tokens. */
export type JsonStyleDef = Record<string, JsonStyleValue>;

/** A stylesheet map — same shape as `StyleSheet.create()` input but with token strings. */
export type JsonStyleSheet = Record<string, JsonStyleDef>;

// ─── Freeze utility ───────────────────────────────────────────────────────────

/**
 * Deep-freeze a JsonStyleSheet (two levels: the map and each style definition).
 *
 * Treat module-level style constants like an immutable CSS bundle — readable by any
 * component, never mutated at runtime, and GC-eligible once the mini-app unloads
 * and all components referencing it are unmounted.
 *
 * @example
 * ```ts
 * export const myStyles = freezeStyleSheet({
 *   container: { flex: 1, backgroundColor: '$theme.bg' },
 * });
 * ```
 */
export function freezeStyleSheet<T extends JsonStyleSheet>(
  sheet: T,
): Readonly<{ readonly [K in keyof T]: Readonly<T[K]> }> {
  for (const def of Object.values(sheet)) {
    Object.freeze(def);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.freeze(sheet) as any;
}

// ─── Core resolver ────────────────────────────────────────────────────────────

type StyleContext = {
  theme: Theme;
  StyleSheet: typeof StyleSheet;
};

/**
 * Resolve a single token string into its real value.
 *
 * Supported namespaces:
 *   `$theme.<key>`       → `theme[key]`   (e.g. `"$theme.primary"`)
 *   `$StyleSheet.<key>`  → `StyleSheet[key]` (e.g. `"$StyleSheet.hairlineWidth"`)
 *
 * Non-token values pass through unchanged.
 */
function resolveValue(value: JsonStyleValue, ctx: StyleContext): unknown {
  if (typeof value !== 'string' || !value.startsWith('$')) return value;
  const dot = value.indexOf('.');
  if (dot === -1) return value;
  const ns = value.slice(1, dot);
  const path = value.slice(dot + 1);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const source: Record<string, any> =
    ns === 'theme'       ? ctx.theme :
    ns === 'StyleSheet'  ? ctx.StyleSheet :
    {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return path.split('.').reduce((acc: any, key) => acc?.[key], source);
}

/**
 * Resolve all token strings in a declarative JSON stylesheet into a new plain object.
 *
 * The returned object contains **only primitive values** copied from the theme and
 * StyleSheet constants — no object references back into `json` survive, so the
 * frozen source map can be GC'd independently of the resolved output.
 */
export function resolveStyles(
  json: JsonStyleSheet,
  ctx: StyleContext,
): Record<string, Record<string, unknown>> {
  const result: Record<string, Record<string, unknown>> = {};
  for (const key of Object.keys(json)) {
    const def = json[key];
    const resolved: Record<string, unknown> = {};
    for (const prop of Object.keys(def)) {
      resolved[prop] = resolveValue(def[prop], ctx);
    }
    result[key] = resolved;
  }
  return result;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Resolve a frozen declarative JSON stylesheet against the current theme and
 * return a `StyleSheet.create()` result.  Re-evaluates only when the theme changes.
 *
 * ### Memory model
 * `json` is read **only during the render call** to resolve tokens into primitive
 * values.  This hook stores only the resolved `StyleSheet` output between renders —
 * no closure or ref holds a reference back into the frozen JSON bundle.  Once the
 * mini-app unloads and its components unmount, the frozen bundle is GC-eligible
 * regardless of prior hook invocations.
 *
 * @example
 * ```ts
 * // styles file — module-level frozen bundle (like a CSS file)
 * export const myStyles = freezeStyleSheet({
 *   container: { flex: 1, backgroundColor: '$theme.bg' },
 *   divider:   { borderBottomWidth: '$StyleSheet.hairlineWidth', borderBottomColor: '$theme.border' },
 * });
 *
 * // component
 * function MyComponent() {
 *   const styles = useDeclarativeStyles(myStyles);
 *   return <View style={styles.container} />;
 * }
 * ```
 */
export function useDeclarativeStyles<K extends string>(
  json: Record<K, JsonStyleDef>,
): Record<K, Record<string, unknown>> {
  const theme = useTheme();

  // These refs store only the resolved RN StyleSheet output (primitive values)
  // and the last seen theme identity.  `json` itself is NOT stored between renders.
  const prevThemeRef = useRef<Theme | null>(null);
  const sheetRef = useRef<Record<K, Record<string, unknown>> | null>(null);

  if (sheetRef.current === null || prevThemeRef.current !== theme) {
    // Resolve token strings → primitive values.  After this block `json` is no longer
    // referenced by anything inside this hook — only `sheetRef.current` survives,
    // and it contains only numbers/strings sourced from the theme and StyleSheet constants.
    const resolved = resolveStyles(json as JsonStyleSheet, { theme, StyleSheet });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sheetRef.current = StyleSheet.create(resolved as any) as Record<K, Record<string, unknown>>;
    prevThemeRef.current = theme;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return sheetRef.current!;
}

